import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Container,
  Header,
  SpaceBetween,
  Spinner,
  Table,
  // eslint-disable-next-line no-unused-vars
  TableProps,
} from '@amzn/awsui-components-react-v3/polaris';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import ListTable, { ListColumn } from '../../Common/ListTable';
import { Messages, RetryAlert } from '../../Common/ErrorMessages';
// eslint-disable-next-line no-unused-vars
import { Locale } from '../../../utils/locales';
import { valueOrDash } from '../../../utils/ReadableConverter';
import { arcLabel } from '../PricingModels/pricingModels';
// eslint-disable-next-line no-unused-vars
import { LoadClient, useLoaded } from '../ScopingModels/useLoaded';
import * as changeSetImpact from './changeSetImpact';

interface ChangeSetImpactPanelProps {
  apiClient: LoadClient;
  changeSetId: string;
  // Labels arcs by locale name; null shows their locale codes.
  locales: Locale[] | null;
  // Shown in the panel's header, e.g. the change set's review buttons.
  actions?: React.ReactNode;
  // Adds each row's organization, for a change set whose models can be several organizations'.
  showOrganization?: boolean;
}

const NOT_FOUND_MESSAGE = 'The change set was not found.';

const CostCell = ({
  estimate,
  note,
  unavailableText,
}: {
  estimate: changeSetImpact.CostEstimate | null;
  note: changeSetImpact.CostNote | null;
  unavailableText: string;
}): JSX.Element =>
  estimate ? (
    <>
      <div>{changeSetImpact.formatAmount(estimate.cost.amount, estimate.cost.currency)}</div>
      <Box variant="small">{changeSetImpact.formatWeightedWords(estimate.weightedWords)}</Box>
      {note && (
        <Box variant="small" color="text-status-info">
          {note.message}
        </Box>
      )}
    </>
  ) : (
    <Box color="text-status-inactive">{unavailableText}</Box>
  );

const TotalComparisonCell = ({
  comparison,
}: {
  comparison: changeSetImpact.PairedCostComparison | null;
}): JSX.Element =>
  comparison ? (
    <>
      <div>{changeSetImpact.formatComparison(comparison)}</div>
      <Box variant="small">{changeSetImpact.formatComparedRows(comparison.rowCount)}</Box>
    </>
  ) : (
    <>{changeSetImpact.NOT_AVAILABLE}</>
  );

const COMPARISONS: { id: keyof changeSetImpact.CostComparisons; header: string }[] = [
  { id: 'liveVsGolden', header: 'Live vs Golden' },
  { id: 'proposedVsGolden', header: 'Proposed vs Golden' },
  { id: 'proposedVsLive', header: 'Proposed vs Live' },
];

const ROW_COMPARISON_COLUMNS = COMPARISONS.map(({ id, header }) => ({
  id,
  header,
  cell: (row: changeSetImpact.GoldenImpactRow): string =>
    changeSetImpact.comparisonText(row.comparisons[id]),
}));

const TOTAL_COMPARISON_COLUMNS = COMPARISONS.map(({ id, header }) => ({
  id,
  header,
  cell: (total: changeSetImpact.CurrencyImpactTotal): JSX.Element => (
    <TotalComparisonCell comparison={total.comparisons[id]} />
  ),
}));

const ORGANIZATION_COLUMN: ListColumn<changeSetImpact.GoldenImpactRow> = {
  id: 'organization',
  header: 'Organization',
  cell: (row): string => row.organizationId,
};

const rowColumns = (
  locales: Locale[] | null,
  showOrganization: boolean
): ListColumn<changeSetImpact.GoldenImpactRow>[] => [
  ...(showOrganization ? [ORGANIZATION_COLUMN] : []),
  { id: 'dataset', header: 'Dataset', cell: (row): string => valueOrDash(row.datasetName) },
  { id: 'arc', header: 'Arc', cell: (row): string => arcLabel(row.arc, locales) },
  { id: 'pricingModel', header: 'Pricing model', cell: (row): string => row.pricingModelName },
  { id: 'scopingModel', header: 'Scoping model', cell: (row): string => row.scopingModelName },
  {
    id: 'proposedModels',
    header: 'Proposed',
    cell: (row): string => changeSetImpact.formatProposedModels(row.proposedModels),
  },
  {
    id: 'golden',
    header: 'Golden cost',
    cell: (row): string => changeSetImpact.formatAmount(row.golden.amount, row.golden.currency),
  },
  {
    id: 'live',
    header: 'Live cost',
    cell: (row): JSX.Element => (
      <CostCell
        estimate={row.live}
        note={row.liveNote}
        unavailableText={valueOrDash(row.liveUnavailable?.message)}
      />
    ),
  },
  {
    id: 'proposed',
    header: 'Proposed cost',
    cell: (row): JSX.Element => (
      <CostCell
        estimate={row.proposed}
        note={row.proposedNote}
        unavailableText={row.proposedNote?.message ?? changeSetImpact.NOT_AVAILABLE}
      />
    ),
  },
  ...ROW_COMPARISON_COLUMNS,
];

const totalText = (sum: changeSetImpact.CostSum | null, currency: string): string =>
  sum === null ? changeSetImpact.NOT_AVAILABLE : changeSetImpact.formatCostSum(sum, currency);

const TOTAL_COLUMNS = [
  {
    id: 'currency',
    header: 'Currency',
    cell: (total: changeSetImpact.CurrencyImpactTotal): string => total.currency,
  },
  {
    id: 'golden',
    header: 'Golden',
    cell: (total: changeSetImpact.CurrencyImpactTotal): string =>
      totalText(total.golden, total.currency),
  },
  {
    id: 'live',
    header: 'Live',
    cell: (total: changeSetImpact.CurrencyImpactTotal): string =>
      totalText(total.live, total.currency),
  },
  {
    id: 'proposed',
    header: 'Proposed',
    cell: (total: changeSetImpact.CurrencyImpactTotal): string =>
      totalText(total.proposed, total.currency),
  },
  ...TOTAL_COMPARISON_COLUMNS,
];

const skippedColumns = (
  showOrganization: boolean
): TableProps.ColumnDefinition<changeSetImpact.SkippedDataset>[] => [
  ...(showOrganization
    ? [
        {
          id: 'organization',
          header: 'Organization',
          cell: (dataset: changeSetImpact.SkippedDataset): string => dataset.organizationId,
        },
      ]
    : []),
  {
    id: 'dataset',
    header: 'Dataset',
    cell: (dataset: changeSetImpact.SkippedDataset): string => dataset.datasetName,
  },
  {
    id: 'rows',
    header: 'Rows',
    cell: (dataset: changeSetImpact.SkippedDataset): number => dataset.rowCount,
  },
  {
    id: 'reasons',
    header: 'Reasons',
    cell: (dataset: changeSetImpact.SkippedDataset): JSX.Element => (
      <Messages messages={dataset.reasons.map(reason => reason.message)} />
    ),
  },
];

const ImpactTables = ({
  impact,
  locales,
  showOrganization,
}: {
  impact: changeSetImpact.ChangeSetImpact;
  locales: Locale[] | null;
  showOrganization: boolean;
}): JSX.Element => {
  const skipped = impact.skippedDatasets;
  if (impact.rows.length === 0 && skipped.length === 0) {
    return (
      <Box id="changeSetImpactEmpty" textAlign="center" color="inherit">
        No golden-set rows use the models this change set writes.
      </Box>
    );
  }
  return (
    <SpaceBetween size="m">
      {impact.rows.length > 0 && (
        <>
          <ListTable
            id="changeSetImpactRows"
            items={impact.rows}
            columns={rowColumns(locales, showOrganization)}
            wrapLines
          />
          <Table
            id="changeSetImpactTotals"
            items={impact.totals}
            columnDefinitions={TOTAL_COLUMNS}
            trackBy="currency"
            header={<Header variant="h3">Totals by currency</Header>}
          />
        </>
      )}
      {skipped.length > 0 && (
        <Table
          id="changeSetImpactSkipped"
          items={skipped}
          columnDefinitions={skippedColumns(showOrganization)}
          trackBy="datasetId"
          wrapLines
          header={
            <Header
              variant="h3"
              counter={`(${skipped.length})`}
              description="Not priced because a model is missing or a live model has no live scheme"
            >
              Skipped datasets
            </Header>
          }
        />
      )}
    </SpaceBetween>
  );
};

/**
 * What a change set does to the cost of the golden-set rows its models appear in, live and as
 * proposed, with totals per currency. Used by the pricing and scoping model pages for their latest
 * pending change set, and by the change set page.
 */
const ChangeSetImpactPanel = ({
  apiClient,
  changeSetId,
  locales,
  actions,
  showOrganization = false,
}: ChangeSetImpactPanelProps): JSX.Element => {
  const impact = useLoaded<unknown>(
    apiClient,
    changeSetImpact.changeSetImpactPath(changeSetId),
    changeSetImpact.describeError
  );
  const loaded = useMemo(
    () => (impact.value ? changeSetImpact.toChangeSetImpact(impact.value) : null),
    [impact.value]
  );
  const failed = impact.missing || impact.errors.length > 0;

  let content: JSX.Element;
  if (failed) {
    content = (
      <RetryAlert
        id="changeSetImpactError"
        header="Could not load the cost impact"
        messages={impact.missing ? [NOT_FOUND_MESSAGE] : impact.errors}
        retryId="retryChangeSetImpact"
        onRetry={impact.reload}
      />
    );
  } else if (loaded) {
    content = (
      <ImpactTables impact={loaded} locales={locales} showOrganization={showOrganization} />
    );
  } else {
    content = (
      <Box id="changeSetImpactLoading" textAlign="center">
        <Spinner /> Loading cost impact
      </Box>
    );
  }

  return (
    <Container
      id="changeSetImpact"
      header={
        <Header
          variant="h2"
          actions={actions}
          description="Golden-set rows that use the models this change set writes, priced live and as it proposes"
        >
          Cost impact
        </Header>
      }
    >
      {content}
    </Container>
  );
};

ChangeSetImpactPanel.propTypes = {
  apiClient: PropTypes.object.isRequired,
  changeSetId: PropTypes.string.isRequired,
  locales: PropTypes.array,
  actions: PropTypes.node,
  showOrganization: PropTypes.bool,
};

export default ChangeSetImpactPanel;
